<template>
  <CCard class="statistical-card auction-statistical-card">
    <CCardHeader
      color="white"
      border-color="white"
      class="auction-statistical-card__legend"
    >
      <div class="auction-statistical-card__legend-item">
        <span />New Auctions
      </div>
      <div class="auction-statistical-card__legend-item">
        <span />Closed Auctions
      </div>
    </CCardHeader>
    <CCardBody
      color="white"
      border-color="white"
      class="auction-statistical-card__body"
    >
      <CChartBarRounded
        type="line"
        class="auction-statistical-card__chart-wrapper"
        :labels="linesConfig.labels"
        :datasets="linesConfig.datasets"
        :options="linesConfig.options"
      />
    </CCardBody>
  </CCard>
</template>

<script>
import { CChartBarRounded } from "@/charts";
import {
  createStatisticalMixin,
  statisticalStore
} from "@/mixins/statisticalMixin";

const newChartColor = "#109CF1";
const closedChartColor = "#F7685B";
const textColor = "#BBBCC1";
const textSize = 12;
const lineHeight = "18px";
const fontFamily = "Poppins";

const gridColor = "#E9ECEF";
const gridLineWidth = 2;
const gridZeroLineColor = "#E9ECEF";
const gridZeroLineWidth = 2;

const newChartStyle = {
  backgroundColor: "transparent",
  borderWidth: 2,
  lineTension: "0.2",
  borderColor: newChartColor,
  hoverBackgroundColor: newChartColor,
  hoverBorderColor: newChartColor,
  pointBackgroundColor: newChartColor,
  pointBorderColor: "transparent",
  pointRadius: 0,
  pointHitRadius: 5
};

const closedChartStyle = {
  backgroundColor: "transparent",
  borderWidth: 2,
  lineTension: "0.2",
  borderDash: [4, 6],
  borderColor: closedChartColor,
  hoverBackgroundColor: closedChartColor,
  hoverBorderColor: closedChartColor,
  pointBackgroundColor: closedChartColor,
  pointBorderColor: "transparent",
  pointRadius: 0,
  pointHitRadius: 5
};

const textStyle = {
  fontFamily: fontFamily,
  fontColor: textColor,
  fontSize: textSize,
  lineHeight: lineHeight
};

const gridStyles = {
  color: gridColor,
  lineWidth: gridLineWidth,
  zeroLineColor: gridZeroLineColor,
  zeroLineWidth: gridZeroLineWidth
};

export default {
  name: "VAuctionsStatisticalCard",

  components: {
    CChartBarRounded
  },

  props: {
    labels: {
      type: Object,
      default: () => ({})
    }
  },

  mixins: [createStatisticalMixin()],

  computed: {
    newAuctions() {
      return (this[statisticalStore.getterTypes.GET_CHARTS] || {})
        .newAuctionsCounts;
    },
    closedAuctions() {
      return (this[statisticalStore.getterTypes.GET_CHARTS] || {})
        .closedAuctionsCounts;
    },
    newAuctionsDataset() {
      if (!this.newAuctions) return [];

      const dataset = this.newAuctions.map(a => a.count);

      if (dataset.length < this.computedLabels.length) {
        this.computedLabels.forEach((label, i) => {
          if (!this.newAuctions.find(a => a.period === label)) {
            dataset.splice(i, 0, 0);
          }
        });
      }

      return dataset;
    },
    closedAuctionsDataset() {
      if (!this.closedAuctions) return [];

      const dataset = this.closedAuctions.map(a => a.count);

      if (dataset.length < this.computedLabels.length) {
        this.computedLabels.forEach((label, i) => {
          if (!this.closedAuctions.find(a => a.period === label)) {
            dataset.splice(i, 0, 0);
          }
        });
      }

      return dataset;
    },
    computedLabels() {
      if (this.labels.labels.length) {
        return this.labels.labels;
      }

      const labels = [];
      if (this.newAuctions) {
        labels.push(...this.newAuctions.map(a => a.period));
      }

      if (this.closedAuctions) {
        this.closedAuctions.forEach(a => {
          if (labels.indexOf(a.period) === -1) {
            labels.push(a.period);
          }
        });
      }

      if (labels.length === 1) {
        labels.splice(0, 0, labels[labels.length - 1] - 1);
      }
      labels.sort((a, b) => a - b);
      return labels;
    },
    linesConfig() {
      return {
        labels: [...this.computedLabels],
        datasets: [
          {
            label: "New Auctions",
            data: [...this.newAuctionsDataset],
            ...newChartStyle
          },
          {
            label: "Closed Auctions",
            data: [...this.closedAuctionsDataset],
            ...closedChartStyle
          }
        ],
        options: {
          layout: {
            padding: {
              top: 50
            }
          },
          scales: {
            xAxes: [
              {
                display: true,
                gridLines: {
                  display: false
                },
                ticks: {
                  display: true,
                  padding: 5,
                  callback: this.labels.formatCallback,
                  ...textStyle
                }
              }
            ],
            yAxes: [
              {
                type: "extendedScale",
                display: true,
                gridLines: {
                  drawTicks: false,
                  ...gridStyles
                },
                ticks: {
                  min: 0,
                  stepSize: this.labels.stepSize.auction,
                  precision: 0,
                  padding: 10,
                  display: true,
                  beginAtZero: true,
                  callback: value => {
                    if (value < 1000) return value;
                    return `${Math.round(value / 100) / 10}K`;
                  },
                  ...textStyle
                }
              }
            ]
          }
        }
      };
    }
  }
};
</script>
